import { useForm, useStore } from '@tanstack/react-form'
import type { ReminderScheduleResponseDto } from '@trellis/api'
import { Button } from '@trellis/roots/button'
import { Checkbox } from '@trellis/roots/checkbox'
import { QuantitySelector } from '@trellis/roots/forms'
import { Input } from '@trellis/roots/input'
import { Label } from '@trellis/roots/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@trellis/roots/select'
import { Tooltip, TooltipContent, TooltipTrigger } from '@trellis/roots/tooltip'
import { Typography } from '@trellis/roots/typography'
import {
  describeReminderSchedule,
  REMINDER_MAX_DAYS_AFTER_DUE,
  REMINDER_MAX_DAYS_BEFORE_DUE,
  REMINDER_MAX_STEPS,
  refineReminderRepeat,
  ReminderRepeatSchema,
  ReminderStepSchema,
  ReminderStepsSchema,
} from '@trellis/shared'
import type { ReminderEmailStage, ReminderRepeat, ReminderStep } from '@trellis/shared'
import { Eye, MoveHorizontal, Plus, X } from 'lucide-react'
import { useState } from 'react'
import type { ReactNode } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'

import {
  useCreateReminderScheduleMutation,
  useUpdateReminderScheduleMutation,
} from '@/hooks/useReminders'
import { cn } from '@/lib/utils'

import { reminderEmailStageFor } from './ReminderEmailPreviewSheet'
import {
  BLUE_PANEL_CLASSES,
  REMINDER_CHANGES_NOTE,
  REMINDER_START_NOTE,
  reminderSendTimeNote,
} from './reminders.constants'
import { ReminderTimeline } from './ReminderTimeline'

const ReminderScheduleFormSchema = z
  .object({
    name: z.string().trim().min(1, { message: 'Give the schedule a name.' }).max(100),
    steps: ReminderStepsSchema,
    repeat: ReminderRepeatSchema.nullable(),
  })
  .superRefine(refineReminderRepeat)

type ReminderScheduleFormValues = z.input<typeof ReminderScheduleFormSchema>
type StepMode = 'before' | 'on' | 'after'

const DEFAULT_REPEAT: ReminderRepeat = { every: 7, max: 3 }
const NEW_STEP_START_DAY = 14

const stepMode = (daysRelativeToDue: number): StepMode =>
  daysRelativeToDue < 0 ? 'before' : daysRelativeToDue === 0 ? 'on' : 'after'

function daysForMode(mode: StepMode, currentDays: number): number {
  if (mode === 'on') return 0
  const magnitude = Math.abs(currentDays)
  if (mode === 'before')
    return -(magnitude > 0 ? Math.min(magnitude, REMINDER_MAX_DAYS_BEFORE_DUE) : 3)
  return magnitude > 0 ? magnitude : 7
}

function nextFreeDay(steps: ReminderStep[]): number {
  const takenDays = new Set(steps.map((step) => step.daysRelativeToDue))
  let day = NEW_STEP_START_DAY
  while (takenDays.has(day) && day < REMINDER_MAX_DAYS_AFTER_DUE) day++
  return day
}

const pluralIssues = (count: number) => (count === 1 ? '1 issue' : `${count} issues`)

/** Issues from the shared schema keyed by path, so each row can show its own messages. */
function collectIssues(values: ReminderScheduleFormValues): Map<string, string[]> {
  const result = ReminderScheduleFormSchema.safeParse(values)
  const issues = new Map<string, string[]>()
  for (const issue of result.error?.issues ?? []) {
    const [root, index] = issue.path
    const key =
      root === 'steps' && typeof index === 'number' ? `steps.${index}` : String(root)
    issues.set(key, [...(issues.get(key) ?? []), issue.message])
  }
  return issues
}

function IconButtonWithTooltip({
  label,
  tooltip,
  disabled = false,
  onClick,
  className,
  children,
}: {
  label: string
  tooltip: string
  disabled?: boolean
  onClick: () => void
  className?: string
  children: ReactNode
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex" tabIndex={disabled ? 0 : undefined}>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={label}
            disabled={disabled}
            onClick={onClick}
            className={cn('text-muted-foreground size-8 rounded-full', className)}
          >
            {children}
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  )
}

/** Starting values for a schedule that doesn't exist yet. */
export type ReminderScheduleDraft = Pick<
  ReminderScheduleResponseDto,
  'name' | 'steps' | 'repeat'
>

interface ReminderScheduleFormBaseProps {
  /**
   * Organisation timezone city shown in the send-time note, e.g. "Sydney". The note is
   * left out when omitted.
   */
  timezoneCity?: string
  /**
   * `embedded` is for use inside a container that already names the schedule, such as a
   * dialog: it drops the outer border, the default schedule's title and the
   * start/send-time notes, which the page then states once. Defaults to `card`.
   */
  variant?: 'card' | 'embedded'
  /** Called after the schedule saves or is created, e.g. to close the dialog around it. */
  onSaved?: () => void
  onPreviewEmail: (stage: ReminderEmailStage) => void
  /** Rendered at the end of the header, e.g. a delete button. */
  headerAction?: ReactNode
  /** Rendered at the start of the footer, opposite Save, e.g. a delete button. */
  footerAction?: ReactNode
  /**
   * Shows a "Use as the default for new invoices" checkbox; pass for global schedules
   * only. Checked and disabled on the current default.
   */
  defaultToggle?: {
    checked: boolean
    disabled: boolean
    onCheckedChange: (isChecked: boolean) => void
  }
}

/**
 * Pass `schedule` to edit an existing one, or `draft` to fill in a new one that is only
 * created when the form is saved.
 */
type ReminderScheduleFormProps = ReminderScheduleFormBaseProps &
  (
    | { schedule: ReminderScheduleResponseDto; draft?: undefined }
    | { schedule?: undefined; draft: ReminderScheduleDraft }
  )

/**
 * Edits one reminder schedule: steps table with channel checkboxes, repeat rule, live
 * plain-English summary and timeline. Validated with the shared schema the API uses.
 */
export function ReminderScheduleForm({
  schedule,
  draft,
  timezoneCity,
  variant = 'card',
  onSaved,
  onPreviewEmail,
  headerAction,
  footerAction,
  defaultToggle,
}: ReminderScheduleFormProps) {
  const isEmbedded = variant === 'embedded'
  const updateMutation = useUpdateReminderScheduleMutation()
  const createMutation = useCreateReminderScheduleMutation()
  const isSaving = updateMutation.isPending || createMutation.isPending
  const initial = schedule ?? draft
  const isDefault = schedule?.isDefault ?? false
  const isClientSchedule = schedule !== undefined && schedule.clientId !== null
  const [rememberedRepeat, setRememberedRepeat] = useState<ReminderRepeat>(
    initial.repeat ?? DEFAULT_REPEAT,
  )

  const form = useForm({
    defaultValues: {
      name: initial.name,
      steps: initial.steps,
      repeat: initial.repeat,
    },
    validators: { onChange: ReminderScheduleFormSchema },
    onSubmit: async ({ value }) => {
      const parsed = ReminderScheduleFormSchema.parse(value)
      const steps = [...parsed.steps].sort(
        (left, right) => left.daysRelativeToDue - right.daysRelativeToDue,
      )
      try {
        if (schedule) {
          await updateMutation.mutateAsync({
            scheduleId: schedule.id,
            payload: {
              ...(isDefault || isClientSchedule ? {} : { name: parsed.name }),
              steps,
              repeat: parsed.repeat,
            },
          })
          toast.success('Schedule saved')
        } else {
          await createMutation.mutateAsync({
            name: parsed.name,
            steps,
            repeat: parsed.repeat,
          })
          toast.success('Schedule created')
        }
        onSaved?.()
      } catch (caughtError) {
        toast.error(schedule ? 'Failed to save schedule' : 'Failed to create schedule', {
          description: caughtError instanceof Error ? caughtError.message : undefined,
        })
      }
    },
  })

  const values = useStore(form.store, (state) => state.values)
  const issues = collectIssues(values)
  const issueCount = [...issues.values()].reduce((total, list) => total + list.length, 0)
  const validSteps = values.steps.filter(
    (step) => ReminderStepSchema.safeParse(step).success,
  )
  const description = describeReminderSchedule(validSteps, values.repeat)
  const isFull = values.steps.length >= REMINDER_MAX_STEPS
  const headingId = `reminder-schedule-${schedule?.id ?? 'new'}`

  function setStep(index: number, patch: Partial<ReminderStep>) {
    form.setFieldValue(
      'steps',
      values.steps.map((step, stepIndex) =>
        stepIndex === index ? { ...step, ...patch } : step,
      ),
    )
  }

  function toggleRepeat(isOn: boolean) {
    if (values.repeat) setRememberedRepeat(values.repeat)
    form.setFieldValue('repeat', isOn ? rememberedRepeat : null)
  }

  const hasVisibleHeader =
    headerAction !== undefined || !isEmbedded || (!isDefault && !isClientSchedule)

  return (
    <form
      id={headingId}
      aria-labelledby={`${headingId}-title`}
      className={cn('scroll-mt-6', !isEmbedded && 'overflow-hidden rounded-xl border')}
      onSubmit={(submitEvent) => {
        submitEvent.preventDefault()
        submitEvent.stopPropagation()
        void form.handleSubmit()
      }}
    >
      {hasVisibleHeader ? (
        <div className="flex flex-wrap items-center justify-between gap-3 border-b p-4">
          <div className="flex min-w-0 flex-wrap items-center gap-2.5">
            {isDefault ? (
              isEmbedded ? (
                <h2 id={`${headingId}-title`} className="sr-only">
                  Default schedule
                </h2>
              ) : (
                <>
                  <Typography variant="h6" as="h2" id={`${headingId}-title`}>
                    Default schedule
                  </Typography>
                  <span className="rounded-full bg-sky-50 px-2.5 text-xs font-medium text-sky-800 dark:bg-sky-950/50 dark:text-sky-200">
                    Used for new invoices
                  </span>
                </>
              )
            ) : isClientSchedule ? (
              <h2 id={`${headingId}-title`} className="sr-only">
                {values.name}
              </h2>
            ) : (
              <>
                <h2 id={`${headingId}-title`} className="sr-only">
                  {values.name || 'Reminder schedule'}
                </h2>
                <Label htmlFor={`${headingId}-name`} className="sr-only">
                  Schedule name
                </Label>
                <Input
                  id={`${headingId}-name`}
                  value={values.name}
                  onChange={(changeEvent) =>
                    form.setFieldValue('name', changeEvent.target.value)
                  }
                  className="h-9 w-full font-semibold sm:w-56"
                />
              </>
            )}
          </div>
          <div className="flex items-center gap-3">{headerAction}</div>
        </div>
      ) : (
        <h2 id={`${headingId}-title`} className="sr-only">
          {isDefault ? 'Default schedule' : values.name}
        </h2>
      )}

      <div className="flex flex-col gap-4 p-4">
        <div>
          <ReminderTimeline
            marks={description.marks}
            editable={{
              steps: values.steps,
              onStepDaysChange: (index, daysRelativeToDue) =>
                setStep(index, { daysRelativeToDue }),
            }}
          />

          <Typography
            variant="label"
            color="muted"
            className="mt-2 flex items-center justify-end gap-1.5"
          >
            <MoveHorizontal className="size-3.5 shrink-0" aria-hidden="true" />
            Drag a dot to change its day
          </Typography>

          {!isEmbedded && (
            <Typography variant="label" color="muted" className="mt-2 block">
              {REMINDER_START_NOTE}
            </Typography>
          )}
        </div>

        <div
          className={cn(
            'flex flex-col gap-1.5 rounded-xl px-4 py-3.5',
            BLUE_PANEL_CLASSES,
          )}
          aria-live="polite"
        >
          <p className="text-sm leading-relaxed">
            {schedule?.enabled !== false
              ? description.summary
              : 'Reminders are switched off. No emails go out until you turn them back on.'}
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <Typography variant="body-sm" className="font-semibold">
            Steps
          </Typography>
          <span className="bg-muted rounded-full px-2.5 text-xs font-medium tabular-nums">
            {values.steps.length} of {REMINDER_MAX_STEPS} steps
          </span>
        </div>

        <div className="overflow-x-auto rounded-xl border">
          <table className="w-full border-collapse text-sm md:min-w-[640px]">
            <thead className="bg-muted/60 text-muted-foreground text-xs max-md:hidden">
              <tr>
                <th scope="col" className="w-8 px-3 py-2 text-left font-medium">
                  #
                </th>
                <th scope="col" className="px-3 py-2 text-left font-medium">
                  When ({REMINDER_MAX_DAYS_BEFORE_DUE} days before to{' '}
                  {REMINDER_MAX_DAYS_AFTER_DUE} days after)
                </th>
                <th scope="col" className="w-24 px-3 py-2 text-center font-medium">
                  Email client
                </th>
                <th scope="col" className="w-24 px-3 py-2 text-center font-medium">
                  Alert me
                </th>
                <th scope="col" className="w-16 px-3 py-2 text-center font-medium">
                  Preview
                </th>
                <th scope="col" className="w-10 px-3 py-2">
                  <span className="sr-only">Remove</span>
                </th>
              </tr>
            </thead>
            <tbody className="max-md:block">
              {values.steps.map((step, index) => {
                const mode = stepMode(step.daysRelativeToDue)
                const rowIssues = issues.get(`steps.${index}`) ?? []
                const stepNumber = index + 1
                return (
                  <tr
                    key={index}
                    className="border-t align-top max-md:flex max-md:flex-wrap max-md:items-center max-md:gap-x-4 max-md:gap-y-1 max-md:p-3 max-md:first:border-t-0"
                  >
                    <td className="px-3 py-2 max-md:p-0">
                      <div className="text-muted-foreground flex h-10 items-center text-xs tabular-nums md:h-8">
                        {stepNumber}
                      </div>
                    </td>
                    <td className="px-3 py-2 max-md:min-w-0 max-md:basis-[calc(100%-2rem)] max-md:p-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <QuantitySelector
                          value={Math.abs(step.daysRelativeToDue)}
                          min={mode === 'on' ? 0 : 1}
                          max={
                            mode === 'on'
                              ? 0
                              : mode === 'before'
                                ? REMINDER_MAX_DAYS_BEFORE_DUE
                                : REMINDER_MAX_DAYS_AFTER_DUE
                          }
                          disabled={mode === 'on'}
                          ariaLabel={`Step ${stepNumber} days`}
                          className={cn(
                            '[&_button]:size-10 [&_input]:h-10 md:[&_button]:size-8 md:[&_input]:h-8',
                            rowIssues.length > 0 && '[&_input]:border-destructive',
                          )}
                          onChange={(days) =>
                            setStep(index, {
                              daysRelativeToDue: mode === 'before' ? -days : days,
                            })
                          }
                        />
                        <span className="text-muted-foreground text-sm">days</span>
                        <Select
                          value={mode}
                          onValueChange={(nextMode) =>
                            setStep(index, {
                              daysRelativeToDue: daysForMode(
                                nextMode as StepMode,
                                step.daysRelativeToDue,
                              ),
                            })
                          }
                        >
                          <SelectTrigger
                            className="h-10 w-full min-w-40 flex-1 md:h-8 md:w-40 md:flex-none"
                            aria-label={`Step ${stepNumber} relative to due date`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="before">before due</SelectItem>
                            <SelectItem value="on">on the due date</SelectItem>
                            <SelectItem value="after">after due</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {rowIssues.length > 0 && (
                        <ul className="text-destructive-text mt-1.5 space-y-0.5 text-xs">
                          {rowIssues.map((message) => (
                            <li key={message}>{message}</li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center max-md:p-0">
                      <div className="flex h-10 items-center justify-center gap-2 md:h-8">
                        <Checkbox
                          aria-label={`Step ${stepNumber} emails the client`}
                          checked={step.emailClient}
                          onCheckedChange={(isChecked) =>
                            setStep(index, { emailClient: isChecked === true })
                          }
                        />
                        <span
                          className="text-muted-foreground text-xs md:hidden"
                          aria-hidden="true"
                        >
                          Email client
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center max-md:p-0">
                      <div className="flex h-10 items-center justify-center gap-2 md:h-8">
                        <Checkbox
                          aria-label={`Step ${stepNumber} alerts me`}
                          checked={step.alertStaff}
                          onCheckedChange={(isChecked) =>
                            setStep(index, { alertStaff: isChecked === true })
                          }
                        />
                        <span
                          className="text-muted-foreground text-xs md:hidden"
                          aria-hidden="true"
                        >
                          Alert me
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center max-md:ml-auto max-md:p-0">
                      <IconButtonWithTooltip
                        label={`Preview step ${stepNumber} email`}
                        tooltip={
                          step.emailClient ? 'Preview email' : 'No email for this step'
                        }
                        disabled={!step.emailClient}
                        onClick={() =>
                          onPreviewEmail(reminderEmailStageFor(step.daysRelativeToDue))
                        }
                      >
                        <Eye className="size-4" />
                      </IconButtonWithTooltip>
                    </td>
                    <td className="px-3 py-2 text-center max-md:p-0">
                      <IconButtonWithTooltip
                        label={`Remove step ${stepNumber}`}
                        tooltip="Remove step"
                        className="hover:text-destructive-text"
                        onClick={() =>
                          form.setFieldValue(
                            'steps',
                            values.steps.filter((_, stepIndex) => stepIndex !== index),
                          )
                        }
                      >
                        <X className="size-4" />
                      </IconButtonWithTooltip>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        {(issues.get('steps') ?? []).map((message) => (
          <p key={message} className="text-destructive-text text-xs">
            {message}
          </p>
        ))}

        <div className="flex flex-wrap items-center justify-end gap-2.5">
          {isFull && (
            <Typography variant="label" color="muted">
              Limit reached. Remove a step to add another.
            </Typography>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex" tabIndex={isFull ? 0 : undefined}>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  disabled={isFull}
                  onClick={() =>
                    form.setFieldValue('steps', [
                      ...values.steps,
                      {
                        daysRelativeToDue: nextFreeDay(values.steps),
                        emailClient: true,
                        alertStaff: false,
                      },
                    ])
                  }
                >
                  <Plus className="mr-1 size-3.5" aria-hidden="true" />
                  Add step
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {isFull
                ? `Limit reached: ${REMINDER_MAX_STEPS} steps`
                : `You can have up to ${REMINDER_MAX_STEPS} steps`}
            </TooltipContent>
          </Tooltip>
        </div>

        <div className="flex items-start gap-2.5 border-t pt-4">
          <Checkbox
            id={`${headingId}-repeat`}
            className="mt-0.5"
            checked={values.repeat !== null}
            onCheckedChange={(isChecked) => toggleRepeat(isChecked === true)}
          />
          <div className="flex flex-1 flex-col gap-2">
            <Label htmlFor={`${headingId}-repeat`} className="font-medium">
              Keep reminding while it stays overdue
            </Label>
            <div
              className={cn(
                'text-muted-foreground flex flex-wrap items-center gap-2 text-sm',
                values.repeat === null && 'opacity-55',
              )}
            >
              After the last email, repeat every
              <QuantitySelector
                value={(values.repeat ?? rememberedRepeat).every}
                min={1}
                max={REMINDER_MAX_DAYS_AFTER_DUE}
                disabled={values.repeat === null}
                ariaLabel="Repeat interval in days"
                className="[&_button]:size-10 md:[&_button]:size-8 [&_input]:h-10 md:[&_input]:h-8"
                onChange={(every) =>
                  form.setFieldValue('repeat', {
                    ...(values.repeat ?? rememberedRepeat),
                    every,
                  })
                }
              />
              days, up to
              <QuantitySelector
                value={(values.repeat ?? rememberedRepeat).max}
                min={1}
                max={REMINDER_MAX_DAYS_AFTER_DUE}
                disabled={values.repeat === null}
                ariaLabel="Number of repeats"
                className="[&_button]:size-10 md:[&_button]:size-8 [&_input]:h-10 md:[&_input]:h-8"
                onChange={(max) =>
                  form.setFieldValue('repeat', {
                    ...(values.repeat ?? rememberedRepeat),
                    max,
                  })
                }
              />
              more times.
            </div>
            {(issues.get('repeat') ?? []).map((message) => (
              <p key={message} className="text-destructive-text text-xs">
                {message}
              </p>
            ))}
          </div>
        </div>
        {defaultToggle && (
          <div className="flex items-start gap-2.5 border-t pt-4">
            <Checkbox
              id={`${headingId}-default`}
              className="mt-0.5"
              checked={defaultToggle.checked}
              disabled={defaultToggle.disabled}
              onCheckedChange={(isChecked) =>
                defaultToggle.onCheckedChange(isChecked === true)
              }
            />
            <div className="flex flex-1 flex-col gap-1">
              <Label htmlFor={`${headingId}-default`} className="font-medium">
                Use as the default for new invoices
              </Label>
              <Typography variant="label" color="muted">
                New invoices use this schedule unless the invoice or its client picks
                another.
              </Typography>
            </div>
          </div>
        )}
        {(issues.get('name') ?? []).map((message) => (
          <p key={message} className="text-destructive-text text-xs">
            {message}
          </p>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t p-4">
        {footerAction}
        {!isEmbedded && timezoneCity && (
          <Typography variant="label" color="muted" className="max-w-[60ch]">
            {reminderSendTimeNote(timezoneCity)} {REMINDER_CHANGES_NOTE}
          </Typography>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {issueCount > 0 && (
            <Typography variant="label" color="muted">
              Fix {pluralIssues(issueCount)} to save
            </Typography>
          )}
          <Button
            type="button"
            variant="outline"
            className="rounded-full"
            onClick={() => onPreviewEmail('BEFORE_DUE')}
          >
            <Eye className="mr-1.5 size-3.5" aria-hidden="true" />
            Preview email
          </Button>
          <Button
            type="submit"
            className="rounded-full"
            disabled={issueCount > 0 || isSaving}
          >
            {isSaving ? 'Saving…' : schedule ? 'Save schedule' : 'Create schedule'}
          </Button>
        </div>
      </div>
    </form>
  )
}
